import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Eraser, Loader2, RefreshCw } from "lucide-react";

export default function TestDataAdmin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/test-data/preview").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/test-data/purge");
      toast.success(`Eliminati ${r.anagrafiche} clienti e ${r.preventivi} preventivi di prova`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="test-data-admin">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Dati di prova</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Clienti creati dai test automatici: nome che inizia con «TEST_» o «Test», oppure email @example.com.
          Vengono cancellati in automatico dopo ogni giro di test; qui puoi controllarli e cancellarli quando vuoi.
        </p>
      </div>
      <Card className="rounded-xl border-border/60 p-5">
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-forest" /> : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-6 text-sm" data-testid="test-data-counts">
              <span><b data-testid="test-data-n-anagrafiche">{data.n_anagrafiche}</b> clienti</span>
              <span><b data-testid="test-data-n-preventivi">{data.n_preventivi}</b> preventivi</span>
              <span><b>{data.n_trattative}</b> trattative</span>
              <span><b>{data.n_intake}</b> moduli dati cliente</span>
              <span><b data-testid="test-data-n-accordi">{data.n_accordi || 0}</b> accordi di prova</span>
            </div>
            <div className="max-h-80 divide-y overflow-auto rounded-lg border" data-testid="test-data-list">
              {data.anagrafiche.length ? data.anagrafiche.map((a) => (
                <div key={a.id} className="flex justify-between gap-3 px-3 py-2 text-sm" data-testid={`test-data-row-${a.id}`}>
                  <span className="font-medium text-forest">{a.ragione_sociale || "—"}</span>
                  <span className="text-muted-foreground">{a.email}</span>
                </div>
              )) : <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="test-data-empty">Nessun dato di prova presente.</div>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={load} data-testid="test-data-refresh" className="gap-1.5"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={busy || !(data.n_anagrafiche || data.n_accordi)} data-testid="test-data-purge-button" className="gap-1.5 bg-red-700 hover:bg-red-800">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eraser className="h-4 w-4" />} Cancella dati di prova
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancellare i dati di prova?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Verranno eliminati {data.n_anagrafiche} clienti e {data.n_preventivi} preventivi collegati. L'operazione resta nel registro Audit e non si può annullare.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="test-data-purge-cancel">Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={purge} data-testid="test-data-purge-confirm" className="bg-red-700 hover:bg-red-800">Cancella</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
